import React, { useEffect, useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { useGraphQLMutation, useGraphQLQuery } from '@tviklax/ui-components';
import {
  SchedulingContainer,
  OrderBarContainer,
  OrderSelectWrapper,
  OrderMetaContainer,
  OrderMetaItem,
  OrderMetaLabel,
  OrderMetaValue,
} from './styles';
import { SchedulingContextMain, SchedulingContextProps } from './SchedulingContextMain';
import type { SchedulingStage, InitialBookingData } from './SchedulingContextTypes';
import { SearchableSelect } from '@tviklax/ui-components';
import { API_BASE_URL } from '../../../shared/constants/env';
import { useUser } from '../../../context/UserContext';
import { fetchSchedules, schedulesToQuotes, resolveLocode } from '../../../services/tviklaExpress';
import type { MappedQuote } from '../../../services/tviklaExpress';

const PRODUCTION_ORDERS_QUERY = gql`
  query SchedulingProductionOrders($limit: Int!, $offset: Int!) {
    productionOrders(limit: $limit, offset: $offset) {
      orderId
      orderNo
      customerName
      supplier
      status
      expectedCompletionDate
      lastUpdated
      notes
    }
  }
`;

const ORDER_SHIPMENTS_QUERY = gql`
  query SchedulingOrderShipments($limit: Int = 300, $offset: Int = 0) {
    orderShipments(limit: $limit, offset: $offset) {
      id
      orderId
      shippingStatus
      shippingProvider
      shippingOptions
      shippingResponse
      trackingNumber
      location
      origin
      destination
      createdAt
      updatedAt
    }
  }
`;

const FOREX_COMPLIANCE_RECORDS_QUERY = gql`
  query SchedulingForexComplianceRecords($limit: Int!, $offset: Int!) {
    forexComplianceRecords(limit: $limit, offset: $offset) {
      id
      checklist {
        requirement
        status
        file
        notes
        required
        uploadedOn
      }
    }
  }
`;

const UPLOAD_FOREX_DOCUMENT_MUTATION = gql`
  mutation SchedulingUploadForexComplianceDocument($input: UploadForexComplianceDocumentInput!) {
    uploadForexComplianceDocument(input: $input) {
      success
      message
    }
  }
`;

interface ProductionOrderRecord {
  orderId: number;
  orderNo: string;
  customerName: string;
  supplier?: string;
  status: string;
  expectedCompletionDate?: string | null;
  lastUpdated: string;
  notes?: string | null;
}

interface OrderShipmentRecord {
  id: number;
  orderId?: number | null;
  shippingStatus?: number | null;
  shippingProvider?: string | null;
  shippingOptions?: string | null;
  shippingResponse?: string | null;
  trackingNumber?: string | null;
  location?: string | null;
  origin?: string | null;
  destination?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

// Map free-text origin/destination to UN/LOCODE (5-char port code)
function toLocode(text: string | null | undefined, fallback: string): string {
  if (!text) return fallback;
  const t = text.toLowerCase();

  // India
  if (t.includes('chennai') || t.includes('madras'))      return 'INMAA';
  if (t.includes('mumbai') || t.includes('bombay'))       return 'INBOM';
  if (t.includes('nhava') || t.includes('nava sheva'))    return 'INNSA';
  if (t.includes('kolkata') || t.includes('calcutta'))    return 'INCCU';
  if (t.includes('india'))                                return 'INMAA'; // default Indian port

  // China
  if (t.includes('shanghai'))                             return 'CNSHA';
  if (t.includes('shenzhen') || t.includes('yantian'))    return 'CNSZX';
  if (t.includes('ningbo'))                               return 'CNNGB';
  if (t.includes('tianjin') || t.includes('beijing'))     return 'CNTJN';
  if (t.includes('guangzhou') || t.includes('canton'))    return 'CNGZU';
  if (t.includes('china') || t.includes('prc'))           return 'CNSHA';

  // USA
  if (t.includes('los angeles') || t.includes('long beach')) return 'USLAX';
  if (t.includes('new york') || t.includes('newark'))     return 'USNYC';
  if (t.includes('houston'))                              return 'USHOU';
  if (t.includes('savannah'))                             return 'USSAV';
  if (t.includes('usa') || t.includes('united states') || t === 'us') return 'USLAX';

  // Europe
  if (t.includes('hamburg') || t.includes('germany'))     return 'DEHAM';
  if (t.includes('rotterdam') || t.includes('netherlands')) return 'NLRTM';
  if (t.includes('antwerp') || t.includes('belgium'))     return 'BEANR';
  if (t.includes('felixstowe') || t.includes('southampton') || t.includes('uk') || t.includes('britain')) return 'GBFXT';
  if (t.includes('eu') || t.includes('europe'))           return 'DEHAM';

  // Southeast Asia / Middle East / Others
  if (t.includes('singapore'))                            return 'SGSIN';
  if (t.includes('ho chi minh') || t.includes('saigon') || t.includes('vietnam')) return 'VNSGN';
  if (t.includes('bangkok') || t.includes('thailand') || t.includes('laem chabang')) return 'THLCH';
  if (t.includes('jakarta') || t.includes('indonesia'))  return 'IDJKT';
  if (t.includes('dubai') || t.includes('uae') || t.includes('jebel ali')) return 'AEJEA';
  if (t.includes('japan') || t.includes('tokyo') || t.includes('yokohama')) return 'JPYOK';
  if (t.includes('korea') || t.includes('busan'))        return 'KRPUS';
  if (t.includes('australia') || t.includes('sydney') || t.includes('melbourne')) return 'AUSYD';

  return fallback;
}

interface SchedulingOrderOption {
  orderId: string;
  customerName: string;
  plant: string;
  backendOrderId?: number;
  productionStatus?: string;
  expectedCompletionDate?: string | null;
  lastUpdate?: string;
  notes?: string | null;
}

interface ForexChecklistItem {
  requirement: string;
  status: 'pending' | 'uploaded' | 'approved' | 'rejected';
  file?: string | null;
  notes?: string | null;
  required: boolean;
  uploadedOn?: string | null;
}

interface ForexComplianceRecord {
  id: string;
  checklist: ForexChecklistItem[];
}

const STAGES: SchedulingStage[] = [
  'Order Confirmed',
  'Queued',
  'Design',
  'Manufacturing',
  'Testing',
  'Production Ready',
  'Loading',
  'Shipped',
  'In Transit',
  'Delivered',
];

export interface SchedulingProps {
  selectedOrderId?: string;
  onOrderChange?: (orderId: string) => void;
  onNavigateToCustoms?: (context?: { orderId: string; carrier: string; vessel: string; eta: string }) => void;
}

// Function to generate scheduling data based on selected order
const generateSchedulingDataForOrder = (
  orderData: {
    orderId: string;
    customerName: string;
    plant: string;
    backendOrderId?: number;
    productionStatus?: string;
    expectedCompletionDate?: string | null;
    lastUpdate?: string;
    notes?: string | null;
  },
  shipmentData: OrderShipmentRecord | undefined,
  forexRecord: ForexComplianceRecord | undefined,
  opts?: { onNavigateToCustoms?: SchedulingProps['onNavigateToCustoms'] },
): SchedulingContextProps => {
  const mapProductionStage = (status?: string): SchedulingStage => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'not-started') return 'Queued';
    if (normalized === 'in-production') return 'Manufacturing';
    if (normalized === 'qc-finishing') return 'Testing';
    if (normalized === 'ready-for-dispatch') return 'Production Ready';
    if (normalized === 'delayed') return 'Manufacturing';
    return 'Order Confirmed';
  };

  const mapShipmentStage = (shippingStatus?: number | null): SchedulingStage | null => {
    if (shippingStatus == null) return null;
    if (shippingStatus >= 7) return 'Delivered';
    if (shippingStatus >= 4) return 'In Transit';
    if (shippingStatus === 3) return 'Shipped';
    if (shippingStatus === 2) return 'Loading';
    return null;
  };

  const inferredStage = mapShipmentStage(shipmentData?.shippingStatus) || mapProductionStage(orderData.productionStatus);
  const stageIndex = Math.max(1, STAGES.indexOf(inferredStage) + 1);
  const healthStatus = (orderData.productionStatus || '').toLowerCase() === 'delayed' ? 'delayed' : 'on-track';
  const lastUpdate = shipmentData?.updatedAt || orderData.lastUpdate || new Date().toISOString();
  const promisedDate = orderData.expectedCompletionDate || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const parseJson = (value?: string | null): Record<string, any> | undefined => {
    if (!value) return undefined;
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : undefined;
    } catch {
      return undefined;
    }
  };

  const shippingOptions = parseJson(shipmentData?.shippingOptions);
  const shippingResponse = parseJson(shipmentData?.shippingResponse);
  const liveCarrier = shipmentData?.shippingProvider || shippingOptions?.carrier || shippingResponse?.carrier;
  const liveVesselName = shippingOptions?.vessel || shippingResponse?.vessel;
  const liveEta = shippingOptions?.eta || shippingResponse?.eta;
  const liveEtd = shippingOptions?.etd || shippingResponse?.etd || shippingOptions?.nextDeparture || shippingResponse?.nextDeparture;
  const liveCost = Number(shippingOptions?.cost ?? shippingResponse?.cost ?? 0);
  const liveDemurrage = Number(shippingOptions?.freeDemurrageDays ?? shippingResponse?.freeDemurrageDays ?? 0);

  const etaDays = (() => {
    if (!liveEta) return 0;
    const etaDate = new Date(liveEta);
    const today = new Date();
    if (Number.isNaN(etaDate.getTime())) return 0;
    return Math.max(0, Math.ceil((etaDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)));
  })();

  const carrierKey = String(liveCarrier || '').toLowerCase().replace(/\s+/g, '-');

  const mappedChecklistDocuments = (forexRecord?.checklist || []).map((item) => {
    const normalizedStatus = item.status?.toLowerCase();
    const docStatus: 'pending' | 'uploaded' | 'verified' = normalizedStatus === 'approved'
      ? 'verified'
      : normalizedStatus === 'uploaded'
        ? 'uploaded'
        : 'pending';
    return {
      name: item.requirement,
      status: docStatus,
      required: Boolean(item.required),
      requiredAfterBooking: item.requirement.trim().toLowerCase() === 'bill of lading',
      file: item.file || undefined,
      uploadedOn: item.uploadedOn || undefined,
      notes: item.notes || undefined,
    };
  });

  const minDocs = mappedChecklistDocuments
    .filter((doc) => doc.required && !doc.requiredAfterBooking)
    .map((doc) => doc.name);

  const originPort = toLocode(shipmentData?.origin, 'INMAA');
  const destinationPort = toLocode(shipmentData?.destination, 'CNSHA');

  return {
    orderId: orderData.orderId,
    backendOrderId: orderData.backendOrderId,
    orderShipmentId: shipmentData?.id,
    originPort,
    destinationPort,
    customerName: orderData.customerName,
    plant: orderData.plant,
    currentStage: inferredStage,
    totalStages: 10,
    stageIndex,
    healthStatus,
    promisedDate,
    lastUpdate,
    nextSystemAction: 'Upload Shipping Bill to enable booking',

    stages: STAGES.map((name, index) => ({
      name,
      completedDate: index < stageIndex ? lastUpdate.slice(0, 10) : undefined,
    })),

    stageDetails: {
      status: orderData.notes || 'Ready for dispatch',
      owner: 'Ops Lead: Priya Menon',
      completion: Math.min(100, stageIndex * 10),
      dueDate: promisedDate,
    },

    vesselsByCarrier: carrierKey && liveVesselName
      ? {
          [carrierKey]: [
            {
              id: `${carrierKey}-live-vessel`,
              name: String(liveVesselName),
              eta: String(liveEta || promisedDate),
              nextDeparture: String(liveEtd || ''),
              capacity: 'N/A',
              containerCount: Number(shippingOptions?.containerCount ?? shippingResponse?.containerCount ?? 0),
              availableContainers: Number(shippingOptions?.availableContainers ?? shippingResponse?.availableContainers ?? 0),
            },
          ],
        }
      : {
          'maersk': [
            {
              id: 'maersk-vessel-1',
              name: 'Maersk Mc-Kinney Moller',
              eta: new Date(Date.now() + 32 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              nextDeparture: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              capacity: '18,270 TEU',
              containerCount: 0,
              availableContainers: 14,
            },
            {
              id: 'maersk-vessel-2',
              name: 'Madrid Maersk',
              eta: new Date(Date.now() + 35 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              nextDeparture: new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              capacity: '20,568 TEU',
              containerCount: 0,
              availableContainers: 8,
            }
          ]
        },

    shippingPrep: {
      readiness: 'partial',
      minDocs,
      overrideEnabled: false,
      documents: mappedChecklistDocuments,
      quotes: liveCarrier
        ? [
            {
              id: carrierKey || 'live-carrier',
              carrier: String(liveCarrier),
              etaDays,
              cost: liveCost,
              transit: 'Sea',
              freeDemurrageDays: liveDemurrage,
              estimatedDeparture: liveEtd ? String(liveEtd) : undefined,
              estimatedArrival: liveEta ? String(liveEta) : undefined,
            },
          ]
        : [
            {
              id: 'maersk-default',
              carrier: 'Maersk',
              etaDays: 32,
              cost: 1450,
              transit: 'Sea',
              freeDemurrageDays: 14,
            }
          ],
    },

    onUserAction: (action: string) => {
      if (action === 'nav:customs' && opts?.onNavigateToCustoms) {
        opts.onNavigateToCustoms({
          orderId: orderData.orderId,
          carrier: String(liveCarrier || orderData.plant || 'Pending carrier assignment'),
          vessel: String(liveVesselName || 'Pending vessel assignment'),
          eta: String(liveEta || promisedDate),
        });
        return;
      }
      console.log(`User action triggered: ${action}`);
    },

    currentUserRole: 'Ops',

    initialBookingConfirmed: liveCost > 0 && liveCarrier && liveVesselName
      ? {
          carrierId: carrierKey || 'maersk',
          carrierName: String(liveCarrier),
          vesselId: `${carrierKey || 'maersk'}-live-vessel`,
          vesselName: String(liveVesselName),
          cost: liveCost,
          etaDays,
          freeDemurrageDays: liveDemurrage || 14,
          vesselEta: String(liveEta || ''),
          vesselEtd: String(liveEtd || ''),
        } as InitialBookingData
      : undefined,

    activityLog: [
      {
        id: 'a1',
        timestamp: lastUpdate,
        type: 'system',
        actor: 'Scheduling System',
        action: shipmentData?.trackingNumber ? 'Shipment updated' : 'Production status updated',
        details: shipmentData?.trackingNumber
          ? `Tracking: ${shipmentData.trackingNumber}`
          : `Stage: ${inferredStage}`,
      },
      {
        id: 'a2',
        timestamp: lastUpdate,
        type: 'system',
        actor: 'Order Management System',
        action: 'Order context synchronized',
        details: `Order: ${orderData.orderId}`,
      },
    ],
  };
};

export const Scheduling: React.FC<SchedulingProps> = ({ selectedOrderId: controlledOrderId, onOrderChange, onNavigateToCustoms }) => {
  const { user } = useUser();
  const { data: productionData } = useGraphQLQuery<{ productionOrders: ProductionOrderRecord[] }>(PRODUCTION_ORDERS_QUERY, {
    variables: { limit: 300, offset: 0 },
    showErrorToast: false,
    fetchPolicy: 'network-only',
  });
  const { data: shipmentData } = useGraphQLQuery<{ orderShipments: OrderShipmentRecord[] }>(ORDER_SHIPMENTS_QUERY, {
    variables: { limit: 400, offset: 0 },
    showErrorToast: false,
    fetchPolicy: 'network-only',
  });
  const { data: forexData, refetch: refetchForexRecords } = useGraphQLQuery<{ forexComplianceRecords: ForexComplianceRecord[] }>(FOREX_COMPLIANCE_RECORDS_QUERY, {
    variables: { limit: 300, offset: 0 },
    showErrorToast: false,
    fetchPolicy: 'network-only',
    pollInterval: 4000,
  });
  const [uploadForexDocument] = useGraphQLMutation(UPLOAD_FOREX_DOCUMENT_MUTATION, {
    showErrorToast: false,
    showSuccessToast: false,
  });

  const getApiOrigin = (): string => API_BASE_URL.replace(/\/graphql\/?$/, '');
  const buildGeneratedDocumentUrl = (relativePath: string): string => {
    const cleanRelative = relativePath
      .replace(/^\/+/, '')
      .split('/')
      .filter(Boolean)
      .map((segment) => encodeURIComponent(segment))
      .join('/');
    return `${getApiOrigin()}/generated-documents/${cleanRelative}`;
  };

  const resolveDocumentUrl = (filePath?: string): string | null => {
    if (!filePath) return null;
    const normalizedPath = filePath.trim();
    if (!normalizedPath) return null;
    if (/^https?:\/\//i.test(normalizedPath)) return normalizedPath;

    if (normalizedPath.startsWith('/generated-documents/')) {
      return `${getApiOrigin()}${normalizedPath}`;
    }

    if (normalizedPath.startsWith('generated_documents/')) {
      return buildGeneratedDocumentUrl(normalizedPath.replace(/^generated_documents\//, ''));
    }

    // Handle legacy absolute filesystem paths that contain generated_documents sub-paths.
    const genDocsMarker = 'generated_documents/';
    const genDocsIdx = normalizedPath.indexOf(genDocsMarker);
    if (genDocsIdx !== -1) {
      return buildGeneratedDocumentUrl(normalizedPath.slice(genDocsIdx + genDocsMarker.length));
    }

    if (!normalizedPath.includes('/') && !normalizedPath.includes('\\') && normalizedPath.includes('.')) {
      const lowerName = normalizedPath.toLowerCase();
      if (lowerName.startsWith('po-')) {
        return buildGeneratedDocumentUrl(`purchase_orders/${normalizedPath}`);
      }
      return buildGeneratedDocumentUrl(`forex_uploads/${normalizedPath}`);
    }

    return normalizedPath;
  };

  const uploadChecklistDocument = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${getApiOrigin()}/api/v1/forex/upload-document`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      let message = 'Document upload failed';
      try {
        const data = await response.json();
        message = data.detail || data.message || message;
      } catch {
        // Ignore JSON parsing errors and keep fallback message.
      }
      throw new Error(message);
    }

    const payload = await response.json();
    const fileUrl = payload.fileUrl || payload.file_url;
    if (!fileUrl || typeof fileUrl !== 'string') {
      throw new Error('Document upload did not return a valid file URL');
    }
    return fileUrl;
  };

  const liveOrders = useMemo<SchedulingOrderOption[]>(() => {
    const records = productionData?.productionOrders || [];
    if (records.length) {
      return records.map((row) => ({
        orderId: row.orderNo,
        backendOrderId: row.orderId,
        customerName: row.customerName || 'Unknown Customer',
        plant: row.supplier || 'Plant not available',
        productionStatus: row.status,
        expectedCompletionDate: row.expectedCompletionDate,
        lastUpdate: row.lastUpdated,
        notes: row.notes,
      }));
    }
    return (forexData?.forexComplianceRecords || []).map((row) => ({
      orderId: row.id,
      customerName: row.id,
      plant: 'Plant not available',
    }));
  }, [forexData?.forexComplianceRecords, productionData?.productionOrders]);

  const forexByOrderRef = useMemo(() => {
    const map = new Map<string, ForexComplianceRecord>();
    (forexData?.forexComplianceRecords || []).forEach((row) => {
      map.set(row.id, row);
    });
    return map;
  }, [forexData?.forexComplianceRecords]);

  const shipmentByOrderId = useMemo(() => {
    const map = new Map<number, OrderShipmentRecord>();
    (shipmentData?.orderShipments || []).forEach((row) => {
      if (!row.orderId) return;
      const existing = map.get(row.orderId);
      const existingDate = existing?.updatedAt || existing?.createdAt || '';
      const currentDate = row.updatedAt || row.createdAt || '';
      if (!existing || currentDate >= existingDate) {
        map.set(row.orderId, row);
      }
    });
    return map;
  }, [shipmentData?.orderShipments]);

  const [localOrderId, setLocalOrderId] = useState<string>(liveOrders[0]?.orderId || '');
  const selectedOrderId = controlledOrderId ?? localOrderId;

  const handleOrderChange = (orderId: string) => {
    setLocalOrderId(orderId);
    onOrderChange?.(orderId);
  };

  const selectedOrder = useMemo(() => {
    return liveOrders.find((o) => o.orderId === selectedOrderId) || liveOrders[0];
  }, [liveOrders, selectedOrderId]);

  const selectedForexRecord = useMemo(() => {
    if (!selectedOrder?.orderId) return undefined;
    if (forexByOrderRef.get(selectedOrder.orderId)) {
      return forexByOrderRef.get(selectedOrder.orderId);
    }
    if (selectedOrder.backendOrderId) {
      const compositeId = `${selectedOrder.orderId}-${selectedOrder.backendOrderId}`;
      return forexByOrderRef.get(compositeId) || forexByOrderRef.get(String(selectedOrder.backendOrderId));
    }
    return undefined;
  }, [forexByOrderRef, selectedOrder?.backendOrderId, selectedOrder?.orderId]);

  const schedulingData = useMemo(() => {
    if (!selectedOrder) {
      return null;
    }
    const shipment = selectedOrder.backendOrderId ? shipmentByOrderId.get(selectedOrder.backendOrderId) : undefined;
    return generateSchedulingDataForOrder(selectedOrder, shipment, selectedForexRecord, { onNavigateToCustoms });
  }, [selectedForexRecord, selectedOrder, shipmentByOrderId, onNavigateToCustoms]);

  // Initialize with all 3 fallback carriers so the UI never shows Maersk-only on first render.
  const [liveQuotes, setLiveQuotes] = useState<MappedQuote[]>(() => schedulesToQuotes([]));

  useEffect(() => {
    if (!schedulingData) return;
    const today = new Date().toISOString().split('T')[0];
    const shipment = selectedOrder?.backendOrderId
      ? shipmentByOrderId.get(selectedOrder.backendOrderId)
      : undefined;
    Promise.all([
      resolveLocode(shipment?.origin ?? '', schedulingData.originPort ?? 'INMAA'),
      resolveLocode(shipment?.destination ?? '', schedulingData.destinationPort ?? 'CNSHA'),
    ]).then(([origin, destination]) => {
      fetchSchedules({ origin_port: origin, destination_port: destination, departure_date: today })
        .then((schedules) => {
          // Always update — schedulesToQuotes returns all 3 carriers with live or fallback data.
          setLiveQuotes(schedulesToQuotes(schedules));
        })
        .catch(() => {});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulingData?.originPort, schedulingData?.destinationPort]);

  // Always use liveQuotes for carrier selection — it always contains all 3 carriers
  // (live schedule where available, indicative fallback otherwise).
  const finalSchedulingData = useMemo(() => {
    if (!schedulingData) return null;
    if (!schedulingData.shippingPrep) return schedulingData;
    return {
      ...schedulingData,
      shippingPrep: { ...schedulingData.shippingPrep, quotes: liveQuotes },
    };
  }, [schedulingData, liveQuotes]);

  const handleUploadShippingDocument = async (documentName: string, file: File) => {
    if (!selectedForexRecord?.id) {
      throw new Error('No linked forex compliance record found for this order');
    }
    const uploadedFileUrl = await uploadChecklistDocument(file);
    const response = await uploadForexDocument({
      variables: {
        input: {
          recordId: selectedForexRecord.id,
          requirement: documentName,
          fileName: uploadedFileUrl,
          actor: user?.name || 'System',
        },
      },
    });
    if (!response.data?.uploadForexComplianceDocument?.success) {
      throw new Error(response.data?.uploadForexComplianceDocument?.message || 'Document upload failed');
    }
    await refetchForexRecords({ limit: 300, offset: 0 });
  };

  const handleReplaceShippingDocument = async (documentName: string, file: File) => {
    await handleUploadShippingDocument(documentName, file);
  };

  if (!selectedOrder || !finalSchedulingData) {
    return (
      <SchedulingContainer>
        <OrderBarContainer>
          <OrderMetaContainer>
            <OrderMetaItem>
              <OrderMetaLabel>No scheduling orders available</OrderMetaLabel>
              <OrderMetaValue>Connect production/forex records to start scheduling.</OrderMetaValue>
            </OrderMetaItem>
          </OrderMetaContainer>
        </OrderBarContainer>
      </SchedulingContainer>
    );
  }

  return (
    <SchedulingContainer>
      <OrderBarContainer>
        <OrderSelectWrapper>
          <SearchableSelect
            options={liveOrders.map((order) => `${order.orderId} — ${order.customerName}`)}
            value={selectedOrderId}
            onChange={(value) => {
              const orderId = value.split(' — ')[0];
              handleOrderChange(orderId);
            }}
            placeholder="Search orders by ID or customer..."
          />
        </OrderSelectWrapper>

        <OrderMetaContainer>
          <OrderMetaItem>
            <OrderMetaLabel>Customer</OrderMetaLabel>
            <OrderMetaValue>{selectedOrder.customerName}</OrderMetaValue>
          </OrderMetaItem>
          <OrderMetaItem>
            <OrderMetaLabel>Plant</OrderMetaLabel>
            <OrderMetaValue>{selectedOrder.plant}</OrderMetaValue>
          </OrderMetaItem>
          <OrderMetaItem>
            <OrderMetaLabel>Current Stage</OrderMetaLabel>
            <OrderMetaValue>{finalSchedulingData.currentStage}</OrderMetaValue>
          </OrderMetaItem>
          <OrderMetaItem>
            <OrderMetaLabel>Promised Date</OrderMetaLabel>
            <OrderMetaValue>{new Date(finalSchedulingData.promisedDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}</OrderMetaValue>
          </OrderMetaItem>
        </OrderMetaContainer>
      </OrderBarContainer>

      <SchedulingContextMain
      key={selectedOrder?.orderId}
      {...finalSchedulingData}
        onUploadShippingDocument={handleUploadShippingDocument}
        onReplaceShippingDocument={handleReplaceShippingDocument}
        onResolveDocumentUrl={resolveDocumentUrl}
      />
    </SchedulingContainer>
  );
};
